import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch } from 'react-redux';
import GavelIcon from '@mui/icons-material/Gavel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { myAuctions } from '../../../apis';
import { showSnackbar } from '../../../store/slices/common.slice';
import { APIResponse, Auction, ListingResponse, TypeSnackbarPayload } from '../../../utilities/types';
import DatagridLoader from '../../elements/datagrid-loader.element';
import {
  getFormattedDate,
  getFormattedPrice,
  getFormattedQuantity,
  parseIntoNumber,
  prepareAndGetMediaURL,
} from '../../../utilities/helper';
import FormModal from '../../modals/form.modal';
import BidRequestGridWidget from '../admin/bid-request-grid.widget';

interface MyAuctionGridWidgetProps {
  onEdit: (auction: any) => void;
  onDelete: (auction: any) => void;
  onView: (auction: any) => void;
  listingType?: string;
}

const MyAuctionGridWidget: React.FC<MyAuctionGridWidgetProps> = ({
  onEdit,
  onDelete,
  onView,
  listingType,
}) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [selectedAuction, setSelectedAuction] = useState<Auction | null>(null);
  const [showBidRequestList, setShowBidRequestList] = useState<boolean>(false);

  const pageSize = 20;

  // Simulate API call (replace with your real API)
  const fetchData = useCallback(
    async (pageNumber: number) => {
      setLoading(true);
      try {
        const response = (await myAuctions({
          page: pageNumber + 1,
          limit: pageSize,
          listing_type: listingType,
        })) as APIResponse<ListingResponse>;
        if (response.status) {
          const items: any = (Array.isArray(response.data?.items) ? response.data.items : []).map(
            (item: any) => {
              return {
                ...item,
                item_name: item.item?.name,
                quantity: getFormattedQuantity(item.quantity, item.quantity_unit),
                start_time: getFormattedDate(item.start_time),
                end_time: getFormattedDate(item.end_time),
                image: item.item?.image,
              };
            }
          );
          setRows(items);
          setRowCount(response.data?.pagination?.total);
        }
      } catch (err: any) {
        const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
        if (err?.data?.message) {
          snakbarpayload.message = err?.data?.message;
        } else if (err?.statusText) {
          snakbarpayload.message = err?.statusText;
        }
        dispatch(showSnackbar(snakbarpayload));
      }
      setLoading(false);
    },
    [dispatch, pageSize, listingType]
  );

  useEffect(() => {
    fetchData(page);
  }, [page, fetchData]);

  // Columns Definition
  const columns = [
    { field: 'custom_id', headerName: 'ID', width: 70 },
    { field: 'title', headerName: 'Title', width: 150 },
    { field: 'item_name', headerName: 'Item Name', width: 150 },
    {
      field: 'price',
      headerName: 'Pricing',
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Stack>
            <Typography variant="body2">
              <strong>Starting Price: </strong>
              {getFormattedPrice(params.row.starting_price)}
            </Typography>
            <Typography variant="body2">
              <strong>Current Price: </strong>
              {getFormattedPrice(params.row.current_price)}
            </Typography>
          </Stack>
        );
      },
    },
    // { field: 'description', headerName: 'Description', flex: 1.5 },
    { field: 'quantity', headerName: 'Quantity' },
    {
      field: 'total_bids',
      headerName: 'Total Bids',
    },
    {
      field: 'end_time',
      headerName: 'Start-End Time',
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Stack>
            <Typography variant="body2">
              <strong>Start Time: </strong>
              {params.row.start_time}
            </Typography>
            <Typography variant="body2">
              <strong>End Time: </strong>
              {params.row.end_time}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'image',
      headerName: 'Image',
      width: 100,
      cellClassName: 'item-img-cell',
      renderCell: (params: any) => (
        <img
          alt="item"
          src={prepareAndGetMediaURL(params.value)}
          style={{ maxWidth: 60, height: 'auto', maxHeight: 60, objectFit: 'cover' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: any) => (
        <Box>
          <IconButton size="small" title="View Details" color="info" onClick={() => onView(params.row)}>
            <GavelIcon />
          </IconButton>
          <IconButton
            // variant="contained"
            size="small"
            color="warning"
            title="Click to view bid requests"
            onClick={() => {
              setSelectedAuction(params.row);
              setShowBidRequestList(true);
            }}
            sx={{ minWidth: 'auto' }}
          >
            <AssignmentTurnedInIcon />
          </IconButton>
          <IconButton size="small" title="Edit Auction" color="primary" onClick={() => onEdit(params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" title="Delete Auction" disabled color="error" onClick={() => onDelete(params.row)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns.map((col) => ({ ...col, sortable: false }))}
        pageSizeOptions={[pageSize]}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        rowCount={rowCount}
        onPaginationModelChange={(newModel: any) => setPage(newModel.page)}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        slots={{
          loadingOverlay: DatagridLoader,
        }}
        sx={{
          // '& .MuiDataGrid-row': {
          //   maxHeight: 'inherit !important',
          //   '--height': '75px !important',
          // },
          '& .item-img-cell': {
            display: 'flex',
          },
        }}
      />
      {(showBidRequestList && selectedAuction) && (
        <FormModal
          open={showBidRequestList}
          onClose={() => {
            setShowBidRequestList(false);
            setSelectedAuction(null);
          }}
          size="md"
          title="Auction Bids Requests"
        >
          <BidRequestGridWidget auction_id={parseIntoNumber(selectedAuction.id)} showAddBuyerForm={false} />
        </FormModal>
      )}
    </Box>
  );
};

export default MyAuctionGridWidget;
